import { debounce } from 'lodash';
import React, { useState, useEffect, SetStateAction } from 'react';
import { useLocation } from 'react-router-dom';
import { Classes } from '@blueprintjs/core';
import { Form, FormInstance, TextInput } from 'blueprint-form';
import {
  faCheckCircle,
  faTimesCircle,
  faSearch,
  faUserCheck
} from '@fortawesome/pro-solid-svg-icons';
import { Row, Col } from '../../../grid';
import Icon from '../../atoms/icon';
import HighlightText from '../../atoms/text/highlight';
import Button from '../../molecules/button';
import Drawer from '../../molecules/drawer';
import Card, { CardBlock } from '../card';
import Pagination from '../pagination';
import { useTerms } from '../../../contexts/terms';
import {
  IPatient,
  useGetOnePatient,
  useGetPatients,
  GetFullName
} from '../../../libs/patient';
import { FormatPhone } from '../../../libs/format';

interface SimpleSearchData {
  keyword: string;
}
interface SelectPatientProps {
  setPatient: (patient: IPatient) => any;
  close: () => any;
}
const PAGE_LIMIT = 8;

const SelectPatient = ({
  close,
  setPatient
}: SelectPatientProps): JSX.Element => {
  const [keyword, setKeyword] = useState<string>(null);
  const [page, setPage] = useState(1);
  const form = new FormInstance<SimpleSearchData>({
    keyword: ''
  });
  const clear = () => {
    setKeyword('');
    form.setData({
      keyword: ''
    });
  };
  const search = debounce((data: SimpleSearchData) => {
    setKeyword(data.keyword);
  }, 100);
  const { patients, total } = useGetPatients({
    name: keyword,
    limit: PAGE_LIMIT,
    page: page - 1
  });
  const selectPatient = (patient: IPatient) => {
    setPatient(patient);
    close();
  };
  // Reset the page when keyword changed
  useEffect(() => {
    setPage(1);
  }, [keyword]);
  return (
    <div className={Classes.DRAWER_BODY}>
      <CardBlock>
        <Form form={form} onSubmit={search}>
          <Row>
            <Col sm={12} md={18}>
              <TextInput
                label="Keyword"
                name="keyword"
                placeholder="Search by name"
                rightElement={
                  keyword ? (
                    <Button
                      variant="minimal"
                      icon={<Icon icon={faTimesCircle} />}
                      onClick={clear}
                    />
                  ) : null
                }
                bare
              />
            </Col>
            <Col sm={12} md={6}>
              <Button
                type="submit"
                icon={<Icon icon={faSearch} />}
                intent="primary"
                fill
              >
                Search
              </Button>
            </Col>
          </Row>
        </Form>
      </CardBlock>
      <CardBlock>
        {patients.map((patient, i) => (
          <Card
            className="mb-4"
            key={i}
            interactive
            onClick={selectPatient.bind(this, patient)}
          >
            <Button
              className="mr-4"
              icon={<Icon className="mr-2" icon={faCheckCircle} />}
              intent="success"
              size="small"
            >
              Select
            </Button>
            <b className="mr-4">
              {HighlightText(GetFullName(patient), keyword, 'highlight')}
            </b>
            <span>{FormatPhone(patient.phone)}</span>
          </Card>
        ))}
        <Pagination
          page={page}
          total={total}
          limit={PAGE_LIMIT}
          onPageChange={(page) => setPage(page)}
        />
      </CardBlock>
    </div>
  );
};

interface SelectPatientDrawerInput {
  setPatient: React.Dispatch<SetStateAction<IPatient>>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<SetStateAction<boolean>>;
}

const SelectPatientDrawer = ({
  setPatient,
  isOpen,
  setIsOpen
}: SelectPatientDrawerInput): JSX.Element => {
  const location = useLocation();
  const { terms } = useTerms();
  // Check whether to select patient outright from URL
  const query = new URLSearchParams(location.search);
  const id = query.get('id');
  const { patient: _patient } = useGetOnePatient(id ? parseInt(id, 10) : null);
  return (
    <Drawer
      title={
        <span>
          <Icon className="mr-4" icon={faUserCheck} />
          {`Select ${terms.patient.capitalized}`}
        </span>
      }
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      portalClassName="z-100"
    >
      <SelectPatient setPatient={setPatient} close={() => setIsOpen(false)} />
    </Drawer>
  );
};

export default SelectPatientDrawer;
